import { useCallback, useEffect } from "react";
import { Box, MenuItem } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  addressLabel,
  enterAddress,
  enterName,
  name,
  paymentMethod,
  save,
  select,
  type,
} from "../../messages";
import { Input } from "../../input/Input";
import Button from "../../button/Button";
import CustomSelect from "../../customSelect/CustomSelect";
import Label from "../../label/Label";
import {
  contactTypes as types,
  contactPaymentMethods as paymentMethods,
  callAPIInterface,
} from "../../constants";
import CustomDivider from "../../divider/Divider";
import InputErrorMessage from "../../inputErrorMessage/InputErrorMessage";
import CommonDrawer from "../../drawer/CommonDrawer";
import Text from "../../text/Text";

function AddContactDrawer({
  formik,
  disabledFields,
  buttonLoader,
  setButtonLoader,
  openDrawer,
  setOpenDrawer,
  modalHeader,
  setActionDrawer,
}) {
  const {
    values,
    handleSubmit,
    isValid,
    dirty,
    setFieldValue,
    resetForm,
    validateForm,
    errors,
    touched,
    setTouched,
  } = formik;

  const decodePaymentAddress = useCallback(async (address) => {
    setButtonLoader(true);
    try {
      const response = await callAPIInterface("POST", "/withdraws/decode", {
        withdraw_request: address,
      });
      return (
        paymentMethods.find(
          (method) => method.withdraw_method === response?.withdraw_method
        ).label || ""
      );
    } catch (error) {
      console.error("Error decoding payment address:", error);
      return "";
    } finally {
      setButtonLoader(false);
    }
  }, []);

  useEffect(() => {
    if (values?.payment_address) {
      const debouncedSearch = setTimeout(async () => {
        const paymentMethod = await decodePaymentAddress(
          values.payment_address
        );

        setFieldValue("payment_method", paymentMethod);
      }, 1000);

      return () => clearTimeout(debouncedSearch);
    } else {
      setFieldValue("payment_method", "");
    }
  }, [values?.payment_address]);

  const selectInput = useCallback(
    (label, options, field) => (
      <Box marginBottom="8px">
        <Label sx={{ marginBottom: "8px !important" }}>{label}</Label>
        <CustomSelect
          disabled={!options.length || disabledFields?.includes(field)}
          name={label}
          displayEmpty
          customClass="dropdown"
          value={values?.[field]}
          MenuProps={{
            id: "filter-asset-menu",
            disableScrollLock: true,
          }}
          onChange={(event) => {
            const {
              target: { value },
            } = event;
            setFieldValue(field, value);
          }}
          renderValue={(selected) => {
            if (!selected) {
              return <Box component="span">{select}</Box>;
            }
            return selected;
          }}
        >
          {options.map((option) => (
            <MenuItem key={option.id} value={option.label}>
              {option.label}
            </MenuItem>
          ))}
        </CustomSelect>
      </Box>
    ),
    [values, setFieldValue, disabledFields]
  );

  const textInput = useCallback(
    (label, placeholder, field, showErrorMsg = false) => (
      <>
        <Input
          fullWidth
          showLabel
          label={label}
          placeholder={placeholder}
          name={label}
          value={values?.[field]}
          type="text"
          onChange={(e) => {
            const { value } = e.target;
            setFieldValue(field, value);
          }}
          inputProps={field === "name" ? { maxLength: 50 } : {}}
          customClass="contact-modal-input"
          disabled={disabledFields?.includes(field)}
          onBlur={() => setTouched({ ...touched, [field]: true })}
          error={touched[field] && Boolean(errors[field])}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.target.blur();
            }
          }}
        />
        {showErrorMsg && touched?.[field] && errors?.[field] && (
          <InputErrorMessage
            inputName={field}
            errors={errors}
            touched={touched}
            sx={{ marginTop: "0px !important" }}
          />
        )}
      </>
    ),
    [values, setFieldValue, disabledFields]
  );

  const renderModalBody = useCallback(
    () => (
      <>
        <Box className="drawer-body">
          <Box
            id="add-contact-form"
            component="form"
            onSubmit={handleSubmit}
            display="flex"
            flexDirection="column"
            gap={2}
          >
            {Object.keys(values).map((field) => {
              switch (field) {
                case "customer_type":
                  return selectInput(type, types, field);
                case "name":
                  return textInput(name, enterName, field, true);
                case "payment_address":
                  return textInput(addressLabel, enterAddress, field);
                case "payment_method":
                  return textInput(paymentMethod, paymentMethod, field);
                default:
                  return null;
              }
            })}
          </Box>
        </Box>
        <Box className="drawer-footer">
          <CustomDivider sx={{ margin: "16px -30px" }} />
          <Button
            form="add-contact-form"
            type="submit"
            loading={buttonLoader}
            disabled={!(isValid && dirty)}
            fullWidth
            variant="contained"
            label={save}
          />
        </Box>
      </>
    ),
    [values, handleSubmit, isValid, dirty, buttonLoader, errors, touched]
  );

  useEffect(() => {
    validateForm();
  }, [values]);

  return (
    <CommonDrawer
      open={openDrawer}
      anchor={"bottom"}
      className="add-contact-drawer"
    >
      <Box className="add-contact-drawer-content">
        <Box className="drawer-header">
          <ArrowBackIcon
            onClick={() => {
              setOpenDrawer(false);
              setActionDrawer?.(false);
              resetForm();
            }}
          ></ArrowBackIcon>
          <Text className="default-text" size={18} font="semibold">
            {modalHeader}
          </Text>
        </Box>
        <Box className="drawer-content">{renderModalBody()}</Box>
      </Box>
    </CommonDrawer>
  );
}

export default AddContactDrawer;
